import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HiArrowLeft,
  HiBan,
  HiCash,
  HiCheckCircle,
  HiCloudUpload,
  HiExclamationCircle,
  HiRefresh,
  HiSearch,
  HiX,
} from 'react-icons/hi';
import apiService from '../services/api';
import { PERMISSAO_MAP } from '../types';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import OpenWhatsAppButton from './OpenWhatsAppButton';
import Notification from './Notification';
import Select from './Select';
import './PendenciasFinanceiras.css';

const formatBrl = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const todayLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatCurrencyInput = (value) => {
  if (!value) return '';
  const numbers = String(value).replace(/\D/g, '');
  if (numbers === '') return '';
  return formatBrl(parseFloat(numbers) / 100);
};

const parseCurrencyToNumber = (value) => {
  if (!value) return 0;
  const numberStr = String(value)
    .replace(/[R$\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
  const parsed = parseFloat(numberStr);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const emptyPaymentForm = (valorRestante) => ({
  valor:
    Number(valorRestante) > 0
      ? formatCurrencyInput(String(Math.round(Number(valorRestante) * 100)))
      : '',
  data: todayLocal(),
  metodo: 'dinheiro',
  comprovantes: [],
});

const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.6) =>
  new Promise((resolve) => {
    if (!file?.type?.startsWith('image/')) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => resolve(file);
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(
              new File([blob], file.name || 'comprovante.jpg', {
                type: blob.type || file.type,
                lastModified: Date.now(),
              })
            );
          },
          file.type === 'image/png' ? 'image/png' : 'image/jpeg',
          quality
        );
      };
      img.src = e.target?.result;
    };
    reader.readAsDataURL(file);
  });

const METODO_OPTIONS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'transferencia', label: 'Transferência' },
];

const ACTION_COPY = {
  cancelar: {
    title: 'Cancelar lançamento',
    hint: 'O lançamento financeiro será cancelado. A carga não será devolvida ao cliente.',
    confirm: 'Cancelar lançamento',
    success: 'Lançamento cancelado com sucesso.',
  },
  devolver: {
    title: 'Devolver carga ao cliente',
    hint: 'A carga será marcada como devolução ao cliente e o lançamento financeiro será cancelado.',
    confirm: 'Devolver carga',
    success: 'Carga devolvida ao cliente.',
  },
};

const PendenciasFinanceiras = () => {
  const { notifications, removeNotification, showError, showSuccess, showWarning } =
    useNotification();
  const { userRole, loading: permissionsLoading } = usePermissions();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [totals, setTotals] = useState({ valor_pendente: 0, quantidade: 0 });
  const [apiPermissoes, setApiPermissoes] = useState(null);
  const [page, setPage] = useState(1);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState(emptyPaymentForm(0));
  const [savingPayment, setSavingPayment] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionJustification, setActionJustification] = useState('');
  const [savingAction, setSavingAction] = useState(false);

  const roleName = PERMISSAO_MAP[Number(userRole?.permissaos_id)] || '';
  const canCancel = apiPermissoes
    ? !!apiPermissoes.cancelar_lancamento
    : !permissionsLoading && (roleName === 'Admin' || roleName === 'Gerente');
  const canReturn = apiPermissoes
    ? !!apiPermissoes.devolver_carga
    : !permissionsLoading && roleName === 'Admin';
  const canPay = apiPermissoes
    ? !!apiPermissoes.registrar_pagamento
    : !permissionsLoading && roleName === 'Admin';

  const loadData = useCallback(
    async (opts = {}) => {
      try {
        if (!opts.silent) setLoading(true);
        const response = await apiService.listPendenciasFinanceiras({
          page,
          per_page: 20,
          search: search.trim() || undefined,
        });
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        setItems(list);
        setMeta(
          response?.meta || {
            current_page: page,
            last_page: 1,
            per_page: 20,
            total: list.length,
          }
        );
        if (response?.totals) {
          setTotals({
            valor_pendente: Number(response.totals.valor_pendente) || 0,
            quantidade: Number(response.totals.quantidade) || 0,
          });
        } else {
          setTotals({
            valor_pendente: list.reduce(
              (sum, item) => sum + (Number(item.valor_restante) || 0),
              0
            ),
            quantidade: Number(response?.meta?.total) || list.length,
          });
        }
        if (response?.permissoes) {
          setApiPermissoes(response.permissoes);
        }
      } catch (error) {
        showError(error?.message || 'Erro ao carregar pendências financeiras');
        setItems([]);
        setTotals({ valor_pendente: 0, quantidade: 0 });
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showError is stable enough; avoid reload loops
    [page, search]
  );

  useEffect(() => {
    const delay = search.trim() ? 400 : 0;
    const timer = setTimeout(() => {
      loadData();
    }, delay);
    return () => clearTimeout(timer);
  }, [loadData, search]);

  useRealtimeRefresh(['payments', 'packages', 'cobrancas', 'invoices'], () =>
    loadData({ silent: true })
  );

  const closePaymentModal = () => {
    (payForm.comprovantes || []).forEach((entry) => {
      if (entry?.preview) URL.revokeObjectURL(entry.preview);
    });
    setPayTarget(null);
    setPayForm(emptyPaymentForm(0));
    setSavingPayment(false);
  };

  const openPaymentModal = (item) => {
    setPayTarget(item);
    setPayForm(emptyPaymentForm(item.valor_restante ?? item.valor_total));
  };

  const closeActionModal = () => {
    setActionTarget(null);
    setActionType(null);
    setActionJustification('');
    setSavingAction(false);
  };

  const openActionModal = (item, type) => {
    setActionTarget(item);
    setActionType(type);
    setActionJustification('');
  };

  const addComprovantes = async (files) => {
    if (!files?.length) return;
    const added = [];
    for (const file of files) {
      const ready = file.type?.startsWith('image/')
        ? await compressImage(file)
        : file;
      added.push({
        file: ready,
        name: ready.name || file.name,
        preview: ready.type?.startsWith('image/')
          ? URL.createObjectURL(ready)
          : null,
      });
    }
    setPayForm((prev) => ({
      ...prev,
      comprovantes: [...(prev.comprovantes || []), ...added],
    }));
  };

  const handleRegisterPayment = async () => {
    if (!payTarget?.id) {
      showWarning('CX não selecionado');
      return;
    }
    if (!payForm.valor || !payForm.data) {
      showWarning('Preencha o valor e a data do pagamento');
      return;
    }
    const comprovantesFiles = (payForm.comprovantes || [])
      .map((c) => (c instanceof File ? c : c?.file))
      .filter((f) => f instanceof File);
    if (comprovantesFiles.length === 0) {
      showWarning('Anexe ao menos um comprovante de pagamento');
      return;
    }

    const valorNumerico = parseCurrencyToNumber(payForm.valor);
    if (valorNumerico <= 0) {
      showWarning('O valor do pagamento deve ser maior que zero');
      return;
    }
    const restante = Number(payTarget.valor_restante ?? payTarget.valor_total) || 0;
    if (restante > 0 && valorNumerico - restante > 0.009) {
      showWarning(`O valor não pode ser maior que o restante (${formatBrl(restante)})`);
      return;
    }

    try {
      setSavingPayment(true);
      const result = await apiService.createPayment({
        pacote_id: payTarget.id,
        valor: valorNumerico.toFixed(2),
        metodo_pagamento_id: null,
        metodo_pagamento: payForm.metodo || 'dinheiro',
        moeda_id: null,
        data_pagamento: payForm.data || undefined,
        comprovantes: comprovantesFiles,
      });
      showSuccess(
        result?.message ||
          `Pagamento de ${formatBrl(valorNumerico)} registrado para ${
            payTarget.codigo_pacote || `CX #${payTarget.id}`
          }`
      );
      closePaymentModal();
      await loadData({ silent: true });
    } catch (error) {
      showError(error?.message || 'Erro ao registrar pagamento. Tente novamente.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleResolveAction = async () => {
    if (!actionTarget?.id || !actionType) {
      showWarning('CX não selecionado');
      return;
    }
    const justificativa = actionJustification.trim();
    if (justificativa.length < 10) {
      showWarning('A justificativa deve ter no mínimo 10 caracteres');
      return;
    }

    try {
      setSavingAction(true);
      const result =
        actionType === 'devolver'
          ? await apiService.devolverCargaPendenciaFinanceira(
              actionTarget.id,
              justificativa
            )
          : await apiService.cancelarLancamentoPendenciaFinanceira(
              actionTarget.id,
              justificativa
            );
      showSuccess(result?.message || ACTION_COPY[actionType].success);
      closeActionModal();
      await loadData({ silent: true });
    } catch (error) {
      showError(error?.message || 'Não foi possível concluir a ação.');
    } finally {
      setSavingAction(false);
    }
  };

  const restanteAlvo = Number(payTarget?.valor_restante ?? payTarget?.valor_total) || 0;
  const valorDigitado = parseCurrencyToNumber(payForm.valor);
  const novoRestante =
    restanteAlvo > 0
      ? Math.max(0, Math.round((restanteAlvo - (valorDigitado > 0 ? valorDigitado : 0)) * 100) / 100)
      : 0;
  const actionCopy = actionType ? ACTION_COPY[actionType] : null;
  const searchActive = Boolean(search.trim());
  const totalPendenteLabel = useMemo(
    () => formatBrl(totals.valor_pendente),
    [totals.valor_pendente]
  );

  return (
    <div className="page-container pfin-page">
      <header className="pfin-hero">
        <div className="pfin-hero__left">
          <span className="pfin-hero__icon" aria-hidden>
            <HiExclamationCircle size={26} />
          </span>
          <div>
            <h1>Pendências financeiras</h1>
            <p>
              CX com pagamento pendente e vinculados a uma invoice. Os mais
              recentes aparecem primeiro.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="pfin-refresh"
          onClick={() => loadData()}
          disabled={loading}
        >
          <HiRefresh size={18} className={loading ? 'is-spinning' : ''} />
          Atualizar
        </button>
      </header>

      <div className="pfin-filters">
        <div className="pfin-search">
          <HiSearch size={18} aria-hidden />
          <input
            type="search"
            placeholder="Buscar por CX, cliente, invoice…"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="pfin-totals" aria-live="polite">
        <div className="pfin-totals__item">
          <span>{searchActive ? 'Pendências filtradas' : 'Pendências'}</span>
          <strong>{totals.quantidade || 0}</strong>
        </div>
        <div className="pfin-totals__item is-money">
          <span>{searchActive ? 'Total pendente da busca' : 'Valor total pendente'}</span>
          <strong>{totalPendenteLabel}</strong>
        </div>
      </div>

      {loading ? (
        <div className="pfin-empty">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="pfin-empty">
          Nenhuma pendência financeira encontrada. Só entram CX com status
          Pendente e invoice vinculada.
        </div>
      ) : (
        <div className="pfin-list">
          {items.map((item) => {
            const invoices = Array.isArray(item.invoices) ? item.invoices : [];
            const invoiceLabel = invoices
              .map((inv) => inv.numero_documento)
              .filter(Boolean)
              .join(' · ') || '—';
            const phone = item.cliente?.telefone;
            return (
              <article key={item.id} className="pfin-row">
                <div className="pfin-row__body">
                  <div className="pfin-row__top">
                    <strong className="pfin-row__code">
                      {item.codigo_pacote || `CX #${item.id}`}
                    </strong>
                    <span className="pfin-badge is-pendente">Pendente</span>
                    {item.entregue && (
                      <span className="pfin-badge is-entregue">Entregue</span>
                    )}
                  </div>
                  <div className="pfin-row__client">
                    {item.cliente?.nome || 'Cliente não informado'}
                  </div>
                  <div className="pfin-row__meta">
                    {formatDateTime(item.created_at)}
                    {' · Invoice '}
                    {invoiceLabel}
                  </div>
                  <div className="pfin-row__meta">
                    Destinatário: {item.destinatario?.nome || '—'}
                    {item.origem || item.destino
                      ? ` · ${item.origem || '—'} → ${item.destino || '—'}`
                      : ''}
                  </div>
                  {phone ? (
                    <OpenWhatsAppButton
                      phone={phone}
                      label={phone}
                      variant="primary"
                      className="pfin-row__whatsapp"
                    />
                  ) : (
                    <div className="pfin-row__meta">Sem telefone do remetente</div>
                  )}
                </div>
                <div className="pfin-row__side">
                  <div className="pfin-row__value">
                    <strong>{formatBrl(item.valor_restante)}</strong>
                    <span>resta de {formatBrl(item.valor_total)}</span>
                  </div>
                  <div className="pfin-row__actions">
                    {canPay && (
                      <button
                        type="button"
                        className="pfin-pay-btn"
                        onClick={() => openPaymentModal(item)}
                      >
                        <HiCash size={16} aria-hidden />
                        Registrar pagamento
                      </button>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        className="pfin-action-btn"
                        onClick={() => openActionModal(item, 'cancelar')}
                      >
                        <HiBan size={16} aria-hidden />
                        Cancelar lançamento
                      </button>
                    )}
                    {canReturn && (
                      <button
                        type="button"
                        className="pfin-action-btn is-return"
                        onClick={() => openActionModal(item, 'devolver')}
                      >
                        <HiArrowLeft size={16} aria-hidden />
                        Devolver ao cliente
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {meta.last_page > 1 && (
        <div className="pfin-pager">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span>
            Página {meta.current_page} de {meta.last_page}
          </span>
          <button
            type="button"
            disabled={page >= meta.last_page || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </button>
        </div>
      )}

      {payTarget && (
        <div
          className="pfin-modal-overlay"
          onClick={() => {
            if (!savingPayment) closePaymentModal();
          }}
        >
          <div
            className="pfin-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pfin-pay-title"
          >
            <header className="pfin-modal__head">
              <h2 id="pfin-pay-title">Registrar pagamento</h2>
              <button
                type="button"
                className="pfin-modal__close"
                onClick={closePaymentModal}
                aria-label="Fechar"
              >
                <HiX size={22} />
              </button>
            </header>

            <div className="pfin-modal__body">
              <label className="pfin-field">
                <span>CX / Cliente</span>
                <input
                  type="text"
                  disabled
                  value={`${payTarget.codigo_pacote || `CX #${payTarget.id}`} — ${
                    payTarget.cliente?.nome || 'Cliente não informado'
                  }`}
                />
              </label>

              <div className="pfin-balance">
                <div>Total {formatBrl(payTarget.valor_total)}</div>
                <div>Pago {formatBrl(payTarget.valor_pago)}</div>
                <div>
                  Restante{' '}
                  <strong>
                    {formatBrl(
                      valorDigitado > 0 ? novoRestante : restanteAlvo
                    )}
                  </strong>
                </div>
              </div>

              <label className="pfin-field">
                <span>Valor deste pagamento *</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={payForm.valor}
                  onChange={(e) =>
                    setPayForm((prev) => ({
                      ...prev,
                      valor: formatCurrencyInput(e.target.value),
                    }))
                  }
                  placeholder="R$ 0,00"
                />
                <small>Pode ser parcial. Máximo: {formatBrl(restanteAlvo)}</small>
              </label>

              <label className="pfin-field">
                <span>Data do pagamento *</span>
                <input
                  type="date"
                  value={payForm.data}
                  onChange={(e) =>
                    setPayForm((prev) => ({ ...prev, data: e.target.value }))
                  }
                />
              </label>

              <div className="pfin-field">
                <span>Método de pagamento</span>
                <Select
                  value={payForm.metodo}
                  onChange={(e) =>
                    setPayForm((prev) => ({ ...prev, metodo: e.target.value }))
                  }
                  options={METODO_OPTIONS}
                />
              </div>

              <div className="pfin-field">
                <span>Comprovantes *</span>
                <div className="pfin-upload-row">
                  <label className="pfin-upload-btn pfin-upload-btn--camera">
                    Tirar foto
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = '';
                        await addComprovantes(files);
                      }}
                    />
                  </label>
                  <label className="pfin-upload-btn">
                    <HiCloudUpload size={18} aria-hidden />
                    Galeria / arquivos
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = '';
                        await addComprovantes(files);
                      }}
                    />
                  </label>
                </div>
                {(payForm.comprovantes || []).length > 0 && (
                  <ul className="pfin-proofs">
                    {payForm.comprovantes.map((entry, idx) => (
                      <li key={`${entry.name || 'file'}-${idx}`}>
                        {entry.preview ? (
                          <img src={entry.preview} alt={entry.name || 'Comprovante'} />
                        ) : (
                          <span className="pfin-proofs__pdf">PDF</span>
                        )}
                        <span>{entry.name || `Arquivo ${idx + 1}`}</span>
                        <button
                          type="button"
                          aria-label="Remover comprovante"
                          onClick={() => {
                            if (entry.preview) URL.revokeObjectURL(entry.preview);
                            setPayForm((prev) => ({
                              ...prev,
                              comprovantes: (prev.comprovantes || []).filter(
                                (_, i) => i !== idx
                              ),
                            }));
                          }}
                        >
                          <HiX size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <small>Pode anexar vários arquivos · JPG, PNG, WEBP ou PDF</small>
              </div>
            </div>

            <footer className="pfin-modal__foot">
              <button
                type="button"
                className="pfin-modal__cancel"
                onClick={closePaymentModal}
                disabled={savingPayment}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pfin-modal__submit"
                onClick={handleRegisterPayment}
                disabled={
                  savingPayment || !(payForm.comprovantes || []).length
                }
              >
                <HiCheckCircle size={18} />
                {savingPayment ? 'Registrando…' : 'Registrar pagamento'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {actionTarget && actionCopy && (
        <div
          className="pfin-modal-overlay"
          onClick={() => {
            if (!savingAction) closeActionModal();
          }}
        >
          <div
            className="pfin-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pfin-action-title"
          >
            <header className="pfin-modal__head">
              <h2 id="pfin-action-title">{actionCopy.title}</h2>
              <button
                type="button"
                className="pfin-modal__close"
                onClick={closeActionModal}
                aria-label="Fechar"
              >
                <HiX size={22} />
              </button>
            </header>
            <div className="pfin-modal__body">
              <label className="pfin-field">
                <span>CX / Cliente</span>
                <input
                  type="text"
                  disabled
                  value={`${actionTarget.codigo_pacote || `CX #${actionTarget.id}`} — ${
                    actionTarget.cliente?.nome || 'Cliente não informado'
                  }`}
                />
              </label>
              <div className="pfin-balance">
                <div>Pendente {formatBrl(actionTarget.valor_restante)}</div>
                <div>{actionCopy.hint}</div>
              </div>
              <label className="pfin-field">
                <span>Justificativa *</span>
                <textarea
                  rows={4}
                  value={actionJustification}
                  onChange={(e) => setActionJustification(e.target.value)}
                  placeholder="Informe o motivo (mínimo 10 caracteres)"
                  minLength={10}
                />
                <small>
                  {actionJustification.trim().length}/10 caracteres mínimos
                </small>
              </label>
            </div>
            <footer className="pfin-modal__foot">
              <button
                type="button"
                className="pfin-modal__cancel"
                onClick={closeActionModal}
                disabled={savingAction}
              >
                Voltar
              </button>
              <button
                type="button"
                className={`pfin-modal__submit ${
                  actionType === 'devolver' ? 'is-return' : 'is-cancel-launch'
                }`}
                onClick={handleResolveAction}
                disabled={savingAction || actionJustification.trim().length < 10}
              >
                {actionType === 'devolver' ? (
                  <HiArrowLeft size={18} />
                ) : (
                  <HiBan size={18} />
                )}
                {savingAction ? 'Salvando…' : actionCopy.confirm}
              </button>
            </footer>
          </div>
        </div>
      )}

      <div className="notifications-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default PendenciasFinanceiras;
